<template>
    <div class="fill-height">
        <v-container>
            <auth-header title="Sign Up" />
            <v-form ref="form" class="auth-form mt-8">
                <v-text-field
                    solo
                    :background-color="background"
                    label="Username"
                    :error-messages="errors.name"
                    v-model="name"
                    :rules="nameRules"
                />
                <v-text-field
                    solo
                    :background-color="background"
                    label="Email"
                    :error-messages="errors.email"
                    v-model="email"
                    :rules="emailRules"
                />
                <v-text-field
                    solo
                    :background-color="background"
                    label="Password"
                    :type="changeInputType"
                    :error-messages="errors.password"
                    v-model="password"
                    :rules="passwordRules"
                >
                    <template #append>
                        <v-icon @click="showPassword = !showPassword">{{ changePasswordIcon }}</v-icon>
                    </template>
                </v-text-field>
                <v-text-field
                    solo
                    :background-color="background"
                    label="Verify Password"
                    :type="changeInputTypeConfirmation"
                    v-model="password_confirmation"
                    v-on:keyup.enter="handleSignUp"
                    :rules="confirmPasswordRules.concat(passwordConfirmationRule)"
                >
                    <template #append>
                        <v-icon @click="showPasswordConfirmation = !showPasswordConfirmation">{{ changePasswordConfirmationIcon }}</v-icon>
                    </template>
                </v-text-field>
                
                <v-text-field
                    solo
                    :background-color="background"
                    label="Referral Code (Optional)"
                    v-model="referral_code"
                    v-on:keyup.enter="handleSignUp"
                />
                  <div class="checkbox-wrapper mb-5">
                      <div class="d-flex align-center">
                          <v-checkbox
                              v-model="isAgeConfirmed"
                              :rules="[v => !!v || 'You must confirm that you are at least 18 years old']"
                              hide-details="auto"
                              color="primary"
                              :error="false"
                              class="mr-2 mt-0 age_confirmation"
                          />
                          <div class="text-body-2">
                            {{ $t('pages.sign_up.age_confirmation') }}  
                          </div>
                      </div>
                  </div>

                <div class="checkbox-wrapper mb-5">
                    <div class="d-flex align-center">
                        <v-checkbox
                            v-model="isTermsConfirmed"
                            hide-details="auto"
                            color="primary"
                            :error="false"
                            class="mr-2 mt-0 terms_condition"
                            :rules="[v => !!v || 'You must agree to the Terms of Service']"
                        ></v-checkbox>
    
                        <div class="text-body-2">
                            {{ $t('pages.sign_up.term_condition') }}  
                            <router-link to="/terms" target="_blank" class="text-decoration-underline">Terms & Conditions</router-link>,
                            <router-link to="/standard-agreement-between-noodzly-and-noodzler" target="_blank" class="text-decoration-underline">Content Provider Agreement</router-link>,
                             and                        
                            <router-link to="/privacy-policy" target="_blank" class="text-decoration-underline">Privacy Policy</router-link>
                        </div>
                     </div>
                </div>
                <div class="checkbox-wrapper mb-5">
                    <div class="d-flex align-center">
                        <v-checkbox
                            v-model="contentAcknowledge"
                            hide-details="auto"
                            color="primary"
                            :error="false"
                            class="mr-2 mt-0 content_acknowledge"
                            :rules="[v => !!v || 'You must agree to the Content Acknowledge']"
                        ></v-checkbox>
    
                        <div class="text-body-2">
                            {{ $t('pages.sign_up.content_acknowledge') }}
                        </div>
                     </div>
                </div>                
                <turnstile-captcha explicit-render @verified="captchaToken = $event" class="pt-3" />
                <span v-if="errors.captcha_token" class="red--text"><strong>* </strong>
                    {{ errors.captcha_token[0] }}
                </span>

                <btn-large
                    class="btn-accent mt-2"
                    @click="handleSignUp"
                    text="Sign Up"
                    :disabled="!isAgeConfirmed || !isTermsConfirmed || !contentAcknowledge"
                />
                <div class="text-caption text-center mt-8">
                    <span>Already have an account?&nbsp;</span>
                    <router-link to="/login" class="link error--text">
                        <span>Login</span>
                    </router-link>
                </div>
            </v-form>
        </v-container>
    </div>
</template>

<script>
import AuthHeader from "@/components/app/auth/AuthHeader";
import TextField from "@/components/app/form/TextField";
import { mapActions, mapMutations } from "vuex";
import SocialLinks from "@/components/app/auth/SocialLinks";
import StaticPageLinks from "@/components/app/common/StaticPageLinks";
import { getReferralCode } from "@/helpers/referral";
import { getUTMTags } from "@/helpers/utmTagging";
import router from "@/router";
import TurnstileCaptcha from '@/components/app/auth/TurnstileCaptcha';
import { getCookie, removeCookie } from "@/helpers/functions";
import { LINK_ACCOUNT_KEY } from "@/configs/constants";

export default {
    name: "SignUp",
    components: {
        SocialLinks,
        StaticPageLinks,
        AuthHeader,
        TextField,
        TurnstileCaptcha,
    },
    data: () => ({
        errors: {},
        name: "",
        email: "",
        password: "",
        password_confirmation: "",
        referral_code: "",
        emailRules: [
            (v) => !!v || 'The email field is required',
            v => /.+@.+/.test(v) || "E-mail must be valid"
        ],
        nameRules: [
            (v) => !!v || 'The name field is required',
        ],
        passwordRules: [
            (v) => !!v || 'The password field is required',
            (v) =>
                (v &&
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(
                    v
                )) ||
                "One capital letter, Special character, Number",
            (v) => v.length >= 8 || "Password should contain at least 8 characters.",
        ],
        confirmPasswordRules: [v => !!v || "Password is required"],
        background: 'rgba(237,237,237, .1)',
        captchaToken: null,
        skipCaptchaOnLogin: true,
        utm_params: [],
        showPassword: false,
        showPasswordConfirmation: false,
        linkToAccount: null,
        isAgeConfirmed: false,
        isTermsConfirmed: false,
        contentAcknowledge: false,
    }),
    computed: {
        passwordConfirmationRule() {
            return () =>
                this.password === this.password_confirmation || "Password must match";
        },
        changePasswordConfirmationIcon() {
            return this.showPasswordConfirmation ? "mdi-eye" : "mdi-eye-off";
        },
        changePasswordIcon() {
            return this.showPassword ? "mdi-eye" : "mdi-eye-off";
        },
        changeInputType() {
            return this.showPassword ? "text" : "password";
        },
        changeInputTypeConfirmation() {
            return this.showPasswordConfirmation ? "text" : "password";
        },
    },
    methods: {
        ...mapMutations(['showSnackBar']),
        ...mapActions(["signUp", "getOwnMainUserData", "getCurrentUserData"]),
        handleSignUp() {
            if (!this.$refs.form.validate()) return;

            if (!this.captchaToken) {
                alert("Please complete the CAPTCHA");
                return;
            }
            const {
                email,
                name,
                password,
                password_confirmation,
                referral_code,
                captchaToken,
                skipCaptchaOnLogin,
                linkToAccount,
                isAgeConfirmed,
                isTermsConfirmed,
                contentAcknowledge,
            } = this;
            this.$auth
                .register({
                    data: {
                        email,
                        name,
                        password,
                        password_confirmation,
                        referral_code,
                        captchaToken,
                        skipCaptchaOnLogin,
                        utm_source: this.utm_params.utm_source,
                        utm_term: this.utm_params.utm_term,
                        utm_campaign: this.utm_params.utm_campaign,
                        utm_medium: this.utm_params.utm_medium,
                        utm_content: this.utm_params.utm_content,
                        linkToAccount,
                        is_age_confirmed: isAgeConfirmed,
                        is_provider_acknowledge: isTermsConfirmed,
                        is_content_acknowledge: contentAcknowledge,
                    },
                    remember: "Rob",
                    staySignedIn: true,
                    autoLogin: true,
                    fetchUser: true,
                })
                .then(() => {
                    localStorage.setItem("email_verified", false);
                    removeCookie(LINK_ACCOUNT_KEY);
                    this.$router
                        .push({ name: "email-verify" })
                        .catch((e) => console.log(e));
                })
                .catch((e) => {
                    console.log(e.response);
                    if (e.response.data.link_error) {
                        this.showSnackBar(e.response.data.link_error);
                    } else {
                        this.errors = e.response.data.errors
                    }
                });
        },
    },
    mounted() {
        this.linkToAccount = getCookie(LINK_ACCOUNT_KEY);
        this.$mixpanel.pageViewed('Signup');
        this.referral_code = getReferralCode();
        this.utm_params = getUTMTags();
    },
};
</script>

<style>
.v-messages {
  position: absolute; 
}

.age_confirmation .v-messages {
  margin: 29px 0px 0px 35px;
}

.terms_condition .v-messages {
  margin: 50px 0px 0px 35px;
}
.content_acknowledge .v-messages {
  margin: 45px 0px 0px 35px;
}
</style>